import { useContext, useRef } from 'react';
import { Rnd } from 'react-rnd';

import '../component.css';
import process_vars from '../intermediate/process_vars';
import SelectionContext from './SelectionContext';
import { collectCandidates, matchAxis } from './snapGuides';

// ElementFrame is the shared canvas shell for every form element in the builder.
// It owns the parts that were previously copy-pasted, byte-for-byte, into each
// primitive/* and intermediate/* element: the <Rnd> wrapper, normalized
// position/size, the magnetic-grid snapping math, drag/resize/hover/select/
// context-menu handlers, the flex skeleton and the border.
//
// Each element supplies:
//   - preset: 'small' | 'large'  (minimum drag/resize footprint)
//   - style:  the element's own Rnd style object, applied verbatim. ElementFrame
//             injects no styling of its own, so elements that intentionally omit
//             the flex skeleton (e.g. Radio) keep their exact layout.
//   - children: the actual rendered widget (innerContent)
//
// The non-editor `pdf` render paths still live in each element and do not use
// this component, so PDF/records rendering is unchanged.

const PRESETS = {
    small: { minW: 0.02, minH: 0.012 },
    large: { minW: 0.05, minH: 0.025 },
};

export default function ElementFrame({
    id,
    comps,
    setter,
    vw,
    gridSize,
    vars,
    setSel,
    setHov,
    setContextMenu,
    magneticGrid,
    magneticGridPartitions,
    preset = 'small',
    style = {},
    aspectRatio,
    lockAspectRatio,
    children,
}) {
    const { minW, minH } = PRESETS[preset] || PRESETS.small;

    const { multiSel, setMultiSel, sel, magneticSnap, setSnapGuides } = useContext(SelectionContext);
    const inGroup = Array.isArray(multiSel) && multiSel.includes(id);
    const isGroup = inGroup && multiSel.length > 1;
    const isSelected = sel === id;

    // Snapshots of every group member's starting position / size, captured when
    // a group drag or resize begins, so we can apply the same delta live.
    const groupDragStart = useRef(null);
    const groupResizeStart = useRef(null);

    // Alignment-snapping state for the current drag.
    const snapCandidates = useRef(null);
    const dragNode = useRef(null);
    const kOffset = useRef(null); // viewport offset of the fraction*vw coordinate origin
    const lastGuideSig = useRef('');

    const minWpx = window.innerWidth * minW;
    const minHpx = window.innerWidth * minH;

    const snapEnabled = () => magneticSnap !== false && typeof setSnapGuides === 'function';

    // Collect candidate alignment lines (excluding the dragged element / group).
    const buildCandidates = () => {
        if (!snapEnabled()) { snapCandidates.current = null; return; }
        const exclude = new Set(isGroup ? multiSel : [id]);
        snapCandidates.current = collectCandidates(comps, exclude, comps[id].page, vw, vars);
        lastGuideSig.current = '';
    };

    // Drag start: build candidates + capture the drag node and the constant
    // offset (K) between the fraction*vw coordinate space and the viewport.
    const beginSnap = (e) => {
        buildCandidates();
        if (!snapEnabled()) { dragNode.current = null; kOffset.current = null; return; }
        const node = (e && e.target && e.target.closest) ? e.target.closest('.comp') : null;
        dragNode.current = node;
        if (node) {
            const r = node.getBoundingClientRect();
            kOffset.current = {
                kx: r.left - process_vars(comps[id].position[0], vars) * vw,
                ky: r.top - process_vars(comps[id].position[1], vars) * vw
            };
        } else {
            kOffset.current = null;
        }
    };

    // Publish guide lines (viewport px) given the matched x/y line coords.
    const publishGuides = (lineX, lineY) => {
        const sig = `${lineX}|${lineY}`;
        if (sig === lastGuideSig.current) return;
        lastGuideSig.current = sig;
        const fv = document.getElementById('form-view');
        const fvr = fv ? fv.getBoundingClientRect() : null;
        const guides = [];
        if (fvr) {
            if (lineX !== null) guides.push({ orientation: 'v', x: lineX, top: fvr.top, bottom: fvr.bottom });
            if (lineY !== null) guides.push({ orientation: 'h', y: lineY, left: fvr.left, right: fvr.right });
        }
        setSnapGuides(guides);
    };

    // Live drag guides from the dragged node's viewport rect (edges + center).
    const updateGuides = () => {
        if (!snapEnabled() || !snapCandidates.current || !dragNode.current || !kOffset.current) return;
        const rect = dragNode.current.getBoundingClientRect();
        const k = kOffset.current;
        const candX = snapCandidates.current.xs.map((c) => ({ v: k.kx + c.v, c: c.c }));
        const candY = snapCandidates.current.ys.map((c) => ({ v: k.ky + c.v, c: c.c }));
        const bx = matchAxis(
            [{ v: rect.left, c: false }, { v: rect.left + rect.width / 2, c: true }, { v: rect.right, c: false }],
            candX
        );
        const by = matchAxis(
            [{ v: rect.top, c: false }, { v: rect.top + rect.height / 2, c: true }, { v: rect.bottom, c: false }],
            candY
        );
        publishGuides(bx ? bx.line : null, by ? by.line : null);
    };

    // Drag stop: snap the final position to element alignment (fraction*vw px).
    const computeStopSnap = (leftPx, topPx) => {
        if (!snapEnabled() || !snapCandidates.current) return { left: null, top: null };
        const w = process_vars(comps[id].size[0], vars) * vw;
        const h = process_vars(comps[id].size[1], vars) * vw;
        const bx = matchAxis(
            [{ v: leftPx, c: false }, { v: leftPx + w / 2, c: true }, { v: leftPx + w, c: false }],
            snapCandidates.current.xs
        );
        const by = matchAxis(
            [{ v: topPx, c: false }, { v: topPx + h / 2, c: true }, { v: topPx + h, c: false }],
            snapCandidates.current.ys
        );
        return {
            left: bx ? leftPx + (bx.line - bx.probeV) : null,
            top: by ? topPx + (by.line - by.probeV) : null
        };
    };

    // Which edges move for a given resize handle direction.
    const movingEdges = (direction) => {
        const d = (direction || '').toLowerCase();
        return { left: d.includes('left'), right: d.includes('right'), top: d.includes('top'), bottom: d.includes('bottom') };
    };

    // Live resize guides for the moving edge(s) (single-element resize only).
    const updateResizeGuides = (direction, ref, position) => {
        if (!snapEnabled() || !snapCandidates.current || isGroup || !ref) return;
        const vrect = ref.getBoundingClientRect();
        const kx = vrect.left - position.x;
        const ky = vrect.top - position.y;
        const m = movingEdges(direction);
        const candX = snapCandidates.current.xs.map((c) => ({ v: kx + c.v, c: c.c }));
        const candY = snapCandidates.current.ys.map((c) => ({ v: ky + c.v, c: c.c }));
        let lineX = null;
        let lineY = null;
        if (m.right) { const b = matchAxis([{ v: vrect.right, c: false }], candX); if (b) lineX = b.line; }
        else if (m.left) { const b = matchAxis([{ v: vrect.left, c: false }], candX); if (b) lineX = b.line; }
        if (m.bottom) { const b = matchAxis([{ v: vrect.bottom, c: false }], candY); if (b) lineY = b.line; }
        else if (m.top) { const b = matchAxis([{ v: vrect.top, c: false }], candY); if (b) lineY = b.line; }
        publishGuides(lineX, lineY);
    };

    // Resize stop: snap the moving edge(s) to alignment; fall back to grid snap.
    const computeResizeSnap = (direction, ref, position) => {
        if (!snapEnabled() || !snapCandidates.current || isGroup) return null;
        const m = movingEdges(direction);
        const leftPx = position.x;
        const topPx = position.y;
        const wPx = ref.offsetWidth;
        const hPx = ref.offsetHeight;
        const right = leftPx + wPx;
        const bottom = topPx + hPx;

        let nLeft;
        let nW;
        const xm = m.right ? matchAxis([{ v: right, c: false }], snapCandidates.current.xs)
            : (m.left ? matchAxis([{ v: leftPx, c: false }], snapCandidates.current.xs) : null);
        if (m.right && xm && (xm.line - leftPx) >= minWpx) { nLeft = leftPx; nW = xm.line - leftPx; }
        else if (m.left && xm && (right - xm.line) >= minWpx) { nLeft = xm.line; nW = right - xm.line; }
        else { nLeft = snap(leftPx); nW = snap(wPx); }

        let nTop;
        let nH;
        const ym = m.bottom ? matchAxis([{ v: bottom, c: false }], snapCandidates.current.ys)
            : (m.top ? matchAxis([{ v: topPx, c: false }], snapCandidates.current.ys) : null);
        if (m.bottom && ym && (ym.line - topPx) >= minHpx) { nTop = topPx; nH = ym.line - topPx; }
        else if (m.top && ym && (bottom - ym.line) >= minHpx) { nTop = ym.line; nH = bottom - ym.line; }
        else { nTop = snap(topPx); nH = snap(hPx); }

        return { leftPx: nLeft, topPx: nTop, wPx: nW, hPx: nH };
    };

    const clearGuides = () => {
        if (typeof setSnapGuides === 'function' && lastGuideSig.current !== '') {
            lastGuideSig.current = '';
            setSnapGuides([]);
        }
        snapCandidates.current = null;
        dragNode.current = null;
        kOffset.current = null;
    };

    // Minimum size expressed as a fraction of vw (matches the px minimums below).
    const minWFrac = (window.innerWidth * minW) / vw;
    const minHFrac = (window.innerWidth * minH) / vw;

    // Rounds a pixel value to the nearest magnetic-grid cell when the grid is on.
    const snap = (value) => {
        if (!magneticGrid) return value;
        const cell = window.innerWidth / magneticGridPartitions;
        return Math.round(value / cell) * cell;
    };

    const locked = !!comps[id].locked;

    return (
        <Rnd
            className={"comp" + (inGroup ? " comp-multiselected" : "") + (isSelected ? " comp-selected" : "") + (locked ? " comp-locked" : "")}
            disableDragging={locked}
            enableResizing={!locked}
            style={style}
            position={{
                x: process_vars(comps[id].position[0], vars) * vw,
                y: process_vars(comps[id].position[1], vars) * vw,
            }}
            size={{
                width: process_vars(comps[id].size[0], vars) * vw,
                height: process_vars(comps[id].size[1], vars) * vw,
            }}
            onDragStart={(e) => {
                beginSnap(e);
                if (isGroup) {
                    // Anchor the group move to the actual pointer position. Using
                    // react-rnd's reported coordinate here is unreliable because
                    // re-rendering the dragged element each tick (to move the
                    // others) desyncs its internal tracking.
                    groupDragStart.current = {
                        mx: (e && e.clientX != null) ? e.clientX : null,
                        my: (e && e.clientY != null) ? e.clientY : null,
                        members: multiSel.map((mid) => ({
                            id: mid,
                            x: process_vars(comps[mid].position[0], vars),
                            y: process_vars(comps[mid].position[1], vars)
                        }))
                    };
                }
            }}
            onDrag={(e, d) => {
                // Live alignment guides (for both single and group drags).
                updateGuides();
                // Live group move: shift the other members by the pointer's delta.
                // The dragged element itself is positioned by react-rnd; we leave
                // it alone until drag stop.
                if (!isGroup || !groupDragStart.current) return;
                const g = groupDragStart.current;
                let dx;
                let dy;
                if (g.mx != null && e && e.clientX != null) {
                    dx = (e.clientX - g.mx) / vw;
                    dy = (e.clientY - g.my) / vw;
                } else {
                    // Fallback (e.g. touch without clientX): use the dragged
                    // element's own start position.
                    const ds = g.members.find((m) => m.id === id);
                    dx = d.x / vw - ds.x;
                    dy = d.y / vw - ds.y;
                }
                let new_comps = [...comps];
                g.members.forEach((m) => {
                    if (m.id === id) return;
                    new_comps[m.id] = { ...new_comps[m.id], position: [m.x + dx, m.y + dy] };
                });
                setter(new_comps);
            }}
            onDragStop={(e, d) => {
                // Prefer element alignment; fall back to grid snap per axis.
                const snapped = computeStopSnap(d.x, d.y);
                const newX = (snapped.left !== null ? snapped.left : snap(d.x)) / vw;
                const newY = (snapped.top !== null ? snapped.top : snap(d.y)) / vw;
                if (isGroup && groupDragStart.current) {
                    // Finalize from the dragged element's authoritative position,
                    // applying the same delta to every member.
                    const g = groupDragStart.current;
                    const ds = g.members.find((m) => m.id === id);
                    const dx = newX - ds.x;
                    const dy = newY - ds.y;
                    let new_comps = [...comps];
                    g.members.forEach((m) => {
                        new_comps[m.id] = { ...new_comps[m.id], position: [m.x + dx, m.y + dy] };
                    });
                    setter(new_comps);
                    groupDragStart.current = null;
                } else {
                    let new_comps = [...comps];
                    new_comps[id].position = [newX, newY];
                    setter(new_comps);
                }
                clearGuides();
            }}
            onResizeStart={() => {
                buildCandidates();
                if (isGroup) {
                    groupResizeStart.current = {
                        members: multiSel.map((mid) => ({
                            id: mid,
                            w: process_vars(comps[mid].size[0], vars),
                            h: process_vars(comps[mid].size[1], vars)
                        }))
                    };
                }
            }}
            onResize={(e, direction, ref, delta, position) => {
                let new_comps = [...comps];
                new_comps[id] = {
                    ...new_comps[id],
                    position: [snap(position.x) / vw, snap(position.y) / vw],
                    size: [snap(ref.offsetWidth) / vw, snap(ref.offsetHeight) / vw]
                };
                // Live group resize: grow/shrink every other member by the same
                // width/height delta as the element being resized.
                if (isGroup && groupResizeStart.current) {
                    const dW = delta.width / vw;
                    const dH = delta.height / vw;
                    groupResizeStart.current.members.forEach((m) => {
                        if (m.id === id) return;
                        new_comps[m.id] = {
                            ...new_comps[m.id],
                            size: [
                                Math.max(minWFrac, m.w + dW),
                                Math.max(minHFrac, m.h + dH)
                            ]
                        };
                    });
                }
                setter(new_comps);
                // Live alignment guides for the moving edge(s).
                updateResizeGuides(direction, ref, position);
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
                groupResizeStart.current = null;
                // Snap the moving edge(s) to element alignment on release.
                const rs = (ref && position) ? computeResizeSnap(direction, ref, position) : null;
                if (rs) {
                    let new_comps = [...comps];
                    new_comps[id] = {
                        ...new_comps[id],
                        position: [rs.leftPx / vw, rs.topPx / vw],
                        size: [rs.wPx / vw, rs.hPx / vw]
                    };
                    setter(new_comps);
                }
                clearGuides();
            }}
            minWidth={window.innerWidth * minW}
            minHeight={window.innerWidth * minH}
            aspectRatio={aspectRatio}
            lockAspectRatio={lockAspectRatio}
            bounds=".form-view"
            dragGrid={[gridSize, gridSize]}
            resizeGrid={[gridSize, gridSize]}
            onMouseEnter={() => setHov(id)}
            onMouseLeave={() => setHov(-1)}
            onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setContextMenu([true, id, [e.clientX, e.clientY]]);
            }}
            onClick={(e) => {
                e.stopPropagation();
                // Shift-click toggles this element's membership in the selection.
                if (e.shiftKey && setMultiSel) {
                    let base = (Array.isArray(multiSel) && multiSel.length > 0)
                        ? [...multiSel]
                        : (sel >= 0 ? [sel] : []);
                    const at = base.indexOf(id);
                    if (at >= 0) base.splice(at, 1);
                    else base.push(id);
                    // Normalize: 0 -> none, 1 -> single select, 2+ -> group.
                    if (base.length === 0) {
                        setMultiSel([]);
                        setSel(-1);
                    } else if (base.length === 1) {
                        setMultiSel([]);
                        setSel(base[0]);
                    } else {
                        setMultiSel(base);
                        setSel(-1);
                    }
                    return;
                }
                // If this element belongs to an active multi-selection, keep the
                // whole group (so click-dragging it doesn't collapse to a single
                // selection). Clicking off any element clears the group.
                if (isGroup) return;
                setSel(id);
                if (setMultiSel) setMultiSel([]);
            }}
        >
            {children}
        </Rnd>
    );
}
